import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ShieldCheck, AlertTriangle, Eye, ExternalLink, Phone, Mail, X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKET_BADGE = {
  eligible: 'bg-emerald-100 text-emerald-800',
  manual_contact_possible: 'bg-amber-100 text-amber-800',
  blocked: 'bg-rose-100 text-rose-800',
};

const BUCKET_LABEL = {
  eligible: 'Email outreach-ready',
  manual_contact_possible: 'Phone-only (manueel)',
  blocked: 'Geblokkeerd',
};

const GATE_LABEL = {
  g01_enrichment_status_verified: 'enrichment_status=verified',
  g02_email_present: 'email aanwezig',
  g03_email_admin_stamp: 'email admin-stamp',
  g04_not_skipped: 'niet skipped',
  g05_not_paused: 'niet paused',
  g06_not_wrong_category: 'niet wrong_category',
  g07_accepted_email_not_role_based: 'email niet role-based',
  g08_outreach_status_not_sent: 'outreach niet verzonden',
  g09_agreement_status_pending: 'agreement_status=pending',
  g10_approved_false: 'approved=false',
  g11_visible_false: 'visible=false',
  g12_source_evidence_present: 'source/evidence aanwezig',
  g13_body_no_customer_data: 'geen klantdata in body',
  g14_category_and_postcode_present: 'category + postcode',
  g15_duplicate_risk_not_high: 'duplicate_risk niet high',
};

function GateChip({ name, ok }) {
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded-full ${
        ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
      }`}
      title={name}
      data-testid={`gate-${name}-${ok ? 'pass' : 'fail'}`}
    >
      {ok ? '✓' : '✗'} {GATE_LABEL[name] || name}
    </span>
  );
}

function SendTestToSelfModal({ open, onClose, provider, testConfig, onConfirm, sending, result }) {
  const [chosenRecipient, setChosenRecipient] = useState(null);
  useEffect(() => {
    if (open && testConfig?.default_test_recipient) setChosenRecipient(testConfig.default_test_recipient);
  }, [open, testConfig]);
  if (!open || !provider) return null;
  const fee = provider.proposed_fee;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="send-test-modal">
      <div className="bg-white rounded-lg max-w-xl w-full shadow-xl">
        <div className="px-5 py-3 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 className="font-medium">Stuur testmail naar jezelf</h3>
            <p className="text-[11px] text-rose-700 mt-1">⚠ Verzending naar INTERN test-adres. Provider ontvangt NIETS.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-send-modal-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          {!result && (
            <>
              <div className="space-y-1.5">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Provider:</span><span className="font-medium" data-testid="send-modal-provider">{provider.company_name}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Originele provider e-mail:</span><span className="font-mono text-[11px]" data-testid="send-modal-original-email">{provider.email || '—'}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Subject:</span><span className="text-[12px]">[TESTMAIL — niet voor provider] Powersure ...</span></div>
                {fee && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Voorgestelde fee:</span><span>€{fee.amount_ex_vat} ex BTW · {fee.category}</span></div>}
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Test-ontvanger (whitelist):</label>
                <select
                  value={chosenRecipient || ''}
                  onChange={e => setChosenRecipient(e.target.value)}
                  className="w-full mt-1 text-xs px-2 py-1.5 border border-border rounded font-mono"
                  data-testid="send-modal-recipient-select"
                >
                  {(testConfig?.allowed_test_recipients || []).map(r => (<option key={r} value={r}>{r}</option>))}
                </select>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[12px]" data-testid="send-modal-warning">
                Dit wordt <strong>NIET</strong> naar de provider verzonden. Er is geen wijziging aan provider-status, agreement of outreach_status. Externe Resend-call gaat naar intern adres.
              </div>
            </>
          )}
          {result && (
            <div className={`rounded p-3 ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`} data-testid="send-result">
              <div className="font-medium mb-1.5">{result.success ? '✅ Testmail verzonden' : '❌ Send mislukt'}</div>
              <div className="text-[11px] space-y-0.5 font-mono">
                <div>send_status: {result.send_status}</div>
                <div>test_recipient: {result.test_recipient}</div>
                {result.resend_id && <div>resend_id: {result.resend_id}</div>}
                {result.error_code && <div>error_code: {result.error_code}</div>}
                {result.error_message && <div>error_message: {result.error_message}</div>}
                <div>event_id: {result.event_id}</div>
                <div>counts_as_external_outreach: {String(result.counts_as_external_outreach)}</div>
                <div>provider_original_email_masked: {result.provider_original_email_masked}</div>
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border bg-slate-50 flex justify-end gap-2">
          {!result && (
            <>
              <button onClick={onClose} disabled={sending} className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted" data-testid="send-modal-cancel-btn">Annuleren</button>
              <button
                onClick={() => onConfirm(chosenRecipient)}
                disabled={sending || !chosenRecipient}
                className="text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1 disabled:opacity-50"
                data-testid="send-modal-confirm-btn"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Verstuur testmail naar {chosenRecipient}
              </button>
            </>
          )}
          {result && (
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded bg-foreground text-background" data-testid="send-modal-close-btn">Sluiten</button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ open, onClose, preview, loading, providerName }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="outreach-preview-modal">
      <div className="bg-white rounded-lg max-w-3xl w-full shadow-xl">
        <div className="px-5 py-3 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 className="font-medium">Mail preview — {providerName}</h3>
            <p className="text-[11px] text-rose-700 mt-1">PREVIEW ONLY · geen Resend-call · externe send vereist aparte expliciete GO</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-preview-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {loading && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
          {!loading && preview && (
            <>
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Subject</div>
                <div className="font-medium" data-testid="preview-subject">{preview.subject}</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Variabelen gebruikt (alle uit whitelist)</div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] font-mono space-y-0.5" data-testid="preview-variables">
                  {Object.entries(preview.variables_used || {}).map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k}</span>: {String(v)}</div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">HTML render</div>
                <div className="border border-border rounded bg-white max-h-72 overflow-y-auto" data-testid="preview-html">
                  <div dangerouslySetInnerHTML={{ __html: preview.body_html }} />
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Plain text</div>
                <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap font-mono" data-testid="preview-text">{preview.body_text}</pre>
              </div>
              {preview.proposed_fee && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[12px]" data-testid="preview-proposed-fee">
                  <strong>Voorgestelde fee:</strong> €{preview.proposed_fee.amount_ex_vat} ex BTW ({preview.proposed_fee.type}) — categorie {preview.proposed_fee.category}
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border bg-slate-50 text-[11px] text-muted-foreground">
          {preview?.warning}
        </div>
      </div>
    </div>
  );
}

export default function OutreachPoolTab({ token }) {
  const [data, setData] = useState({ eligible: [], blocked: [], manual_contact_possible: [], counts: {}, proposed_fee_table: {} });
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState('eligible');
  const [filter, setFilter] = useState({ category: '', region: '', search: '', missing_email_only: false, role_blocked_only: false });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewProviderName, setPreviewProviderName] = useState('');
  const [testConfig, setTestConfig] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendModalProvider, setSendModalProvider] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadPool = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/provider-outreach/pool?limit=500&include_blocked=true`, { headers: authH });
      setData(r.data);
    } catch (e) {
      toast.error('Kon pool niet laden');
    } finally {
      setLoading(false);
    }
  }, [authH]);

  useEffect(() => { loadPool(); }, [loadPool]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/provider-outreach/test-config`, { headers: authH });
        setTestConfig(r.data);
      } catch (e) {
        // silent
      }
    })();
  }, [authH]);

  const openSendModal = (provider) => {
    setSendModalProvider(provider);
    setSendResult(null);
    setSendModalOpen(true);
  };

  const closeSendModal = () => {
    setSendModalOpen(false);
    setSendModalProvider(null);
    setSendResult(null);
  };

  const confirmSendTest = async (recipient) => {
    if (!sendModalProvider) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/provider-outreach/send-test-to-self`,
        { provider_id: sendModalProvider.provider_id, test_recipient: recipient },
        { headers: authH });
      setSendResult(r.data);
      if (r.data.success) {
        toast.success(`Testmail verzonden naar ${r.data.test_recipient}`);
      } else {
        toast.error(`Send mislukt: ${r.data.error_message || r.data.send_status}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setSendResult({ success: false, error_message: detail, send_status: 'send_failed' });
      toast.error(`Send fout: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  const openPreview = async (providerId, providerName) => {
    setPreviewProviderName(providerName);
    setPreviewData(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/admin/provider-outreach/preview?provider_id=${providerId}`, { headers: authH });
      setPreviewData(r.data);
    } catch (e) {
      toast.error(`Preview fout: ${e?.response?.data?.detail || e.message}`);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const visible = useMemo(() => {
    const src = data[bucket] || [];
    return src.filter(p => {
      if (filter.category && p.category !== filter.category) return false;
      if (filter.region && String(p.region_prefix) !== String(filter.region)) return false;
      if (filter.search) {
        const s = filter.search.toLowerCase();
        const hay = `${p.company_name || ''} ${p.city || ''} ${p.email || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (filter.missing_email_only && p.email) return false;
      if (filter.role_blocked_only && !(p.failed_gates || []).includes('g07_accepted_email_not_role_based')) return false;
      return true;
    });
  }, [data, bucket, filter]);

  const distinctCategories = useMemo(() => {
    const s = new Set();
    ['eligible', 'blocked', 'manual_contact_possible'].forEach(b => (data[b] || []).forEach(p => p.category && s.add(p.category)));
    return [...s].sort();
  }, [data]);

  const distinctRegions = useMemo(() => {
    const s = new Set();
    ['eligible', 'blocked', 'manual_contact_possible'].forEach(b => (data[b] || []).forEach(p => p.region_prefix && s.add(String(p.region_prefix))));
    return [...s].sort();
  }, [data]);

  return (
    <div className="space-y-4" data-testid="outreach-pool-tab">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900 flex items-start gap-2" data-testid="outreach-safety-banner">
        <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Veilige modus:</strong> deze tab muteert GEEN provider-state en verstuurt GEEN mail naar externe providers.
          Mail-preview is render-only. "Send test to self" verstuurt alleen naar interne Powersure-adressen voor template-validatie.
          Externe provider-send vereist aparte expliciete GO en zal in een latere release verschijnen.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium">Outreach Pool</h2>
          <span className="text-xs text-muted-foreground" data-testid="outreach-counts">
            eligible: <strong className="text-emerald-700">{data.counts?.eligible || 0}</strong> ·
            manual contact: <strong className="text-amber-700">{data.counts?.manual_contact_possible || 0}</strong> ·
            blocked: <strong className="text-rose-700">{data.counts?.blocked || 0}</strong>
          </span>
        </div>
        <button
          onClick={loadPool}
          disabled={loading}
          className="text-xs px-2 py-1 rounded border border-border hover:bg-muted flex items-center gap-1"
          data-testid="outreach-refresh-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Bucket tabs */}
      <div className="flex gap-2 border-b border-border" data-testid="bucket-tabs">
        {Object.keys(BUCKET_LABEL).map(b => (
          <button
            key={b}
            onClick={() => setBucket(b)}
            className={`text-xs px-3 py-2 border-b-2 ${
              bucket === b ? 'border-foreground text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`bucket-tab-${b}`}
          >
            {BUCKET_LABEL[b]} ({data[b]?.length || 0})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="outreach-filters">
        <input
          type="text"
          placeholder="zoek bedrijf / email / stad"
          value={filter.search}
          onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
          className="text-xs px-2 py-1 border border-border rounded w-56"
          data-testid="filter-search"
        />
        <select value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))} className="text-xs px-2 py-1 border border-border rounded" data-testid="filter-category">
          <option value="">alle categorieën</option>
          {distinctCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.region} onChange={e => setFilter(f => ({ ...f, region: e.target.value }))} className="text-xs px-2 py-1 border border-border rounded" data-testid="filter-region">
          <option value="">alle regio's</option>
          {distinctRegions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={filter.missing_email_only} onChange={e => setFilter(f => ({ ...f, missing_email_only: e.target.checked }))} data-testid="filter-missing-email" />
          alleen zonder email
        </label>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={filter.role_blocked_only} onChange={e => setFilter(f => ({ ...f, role_blocked_only: e.target.checked }))} data-testid="filter-role-blocked" />
          alleen role-based blocked
        </label>
      </div>

      {/* Provider cards */}
      <div className="space-y-2" data-testid="outreach-list">
        {visible.length === 0 && !loading && (
          <div className="text-sm text-muted-foreground text-center py-8" data-testid="outreach-empty">Geen providers in deze bucket / filter.</div>
        )}
        {visible.map(p => (
          <div key={p.provider_id} className="border border-border rounded-lg p-3 bg-white" data-testid={`row-${p.provider_id}`}>
            <div className="flex items-start gap-3 justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium" data-testid="row-company-name">{p.company_name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${BUCKET_BADGE[p.bucket]}`} data-testid="row-bucket-badge">
                    {BUCKET_LABEL[p.bucket]}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{p.category_nl || p.category} · {p.region_label}</span>
                </div>
                <div className="text-[11px] flex flex-wrap gap-3 mt-1">
                  {p.email && (
                    <span className="flex items-center gap-1" data-testid="row-email">
                      <Mail className="w-3 h-3 text-emerald-600" />{p.email}
                    </span>
                  )}
                  {p.phone && (
                    <span className="flex items-center gap-1" data-testid="row-phone">
                      <Phone className="w-3 h-3 text-sky-600" />{p.phone}
                    </span>
                  )}
                  {p.source_url && (
                    <a href={p.source_url} target="_blank" rel="noreferrer noopener" className="text-sky-700 hover:underline flex items-center gap-1" data-testid="row-source-url">
                      <ExternalLink className="w-3 h-3" /> source
                    </a>
                  )}
                  {p.proposed_fee && (
                    <span className="text-amber-700">fee: €{p.proposed_fee.amount_ex_vat} ex BTW · {p.proposed_fee.category || 'default'}</span>
                  )}
                </div>
                {p.evidence_snippet && (
                  <div className="text-[10px] bg-slate-50 border border-slate-200 rounded p-1 mt-1.5 font-mono break-all">{p.evidence_snippet}</div>
                )}
                {p.failed_gates && p.failed_gates.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid="row-failed-gates">
                    {p.failed_gates.map(g => <GateChip key={g} name={g} ok={false} />)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                <button
                  onClick={() => openPreview(p.provider_id, p.company_name)}
                  className="text-[11px] px-2 py-1 rounded bg-sky-100 text-sky-800 hover:bg-sky-200 flex items-center gap-1"
                  data-testid="preview-mail-btn"
                >
                  <Eye className="w-3 h-3" /> Preview mail
                </button>
                {p.bucket === 'eligible' && (
                  <button
                    onClick={() => openSendModal(p)}
                    className="text-[11px] px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 flex items-center gap-1"
                    data-testid="send-test-to-self-btn"
                    title="Stuurt testmail naar INTERN admin-adres — NIET naar provider"
                  >
                    <Send className="w-3 h-3" /> Send test to self
                  </button>
                )}
                <div className="text-[10px] text-muted-foreground text-right" data-testid="row-safety-summary">
                  {p.safety?.approved === false && p.safety?.visible === false ? '✓ safety pins ok' : '⚠ safety check'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={previewData}
        loading={previewLoading}
        providerName={previewProviderName}
      />

      <SendTestToSelfModal
        open={sendModalOpen}
        onClose={closeSendModal}
        provider={sendModalProvider}
        testConfig={testConfig}
        onConfirm={confirmSendTest}
        sending={sending}
        result={sendResult}
      />
    </div>
  );
}
